import { visit } from "@hotwired/turbo";
import { Download } from "lucide-react";
import ReactPlayer from "react-player";
import { AvatarAppearance } from "../../types/avatar_appearances.d";
import SpeakerIdentificationTable from "./SpeakerIdentificationTable";
import Breadcrumbs from "../shared/Breadcrumbs";

interface AvatarAppearanceShowProps {
  avatarAppearance: AvatarAppearance;
}

// #STYLE_GUIDE: Show component displays single record details
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
export default function AvatarAppearanceShow({
  avatarAppearance,
}: AvatarAppearanceShowProps) {
  const handleBack = () => {
    // #TURBO: Use Turbo.visit for navigation
    if (avatarAppearance.avatarId) {
      visit(`/avatars/${avatarAppearance.avatarId}`);
    } else {
      visit("/dashboard/index");
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          ...(avatarAppearance.avatarId && avatarAppearance.avatarDisplayName
            ? [
                {
                  label: avatarAppearance.avatarDisplayName,
                  path: `/avatars/${avatarAppearance.avatarId}`,
                },
              ]
            : []),
          { label: avatarAppearance.name || "Avatar Appearance" },
        ]}
      />

      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">AvatarAppearance Details</h1>
        <button onClick={handleBack} className="btn btn-outline">
          {avatarAppearance.avatarId ? "Back to Avatar" : "Back to Dashboard"}
        </button>
      </div>

      {/* Details card */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">
                <span className="label-text font-semibold">ID</span>
              </label>
              <p className="text-base-content">{avatarAppearance.id}</p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Avatar ID</span>
              </label>
              <p className="text-base-content">
                {avatarAppearance.avatarId || "Not set"}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Name</span>
              </label>
              <p className="text-base-content">
                {avatarAppearance.name || "Not set"}
              </p>
            </div>

            {avatarAppearance.mediaFileUrl && (
              <div className="md:col-span-2">
                <label className="label">
                  <span className="label-text font-semibold">Media File</span>
                </label>
                <div className="space-y-4">
                  <ReactPlayer
                    src={avatarAppearance.mediaFileUrl}
                    controls
                    width="100%"
                    height="auto"
                    style={{ aspectRatio: "16/9", maxWidth: "800px" }}
                  />
                  <a
                    href={avatarAppearance.mediaFileUrl}
                    download
                    className="btn btn-outline btn-sm gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Media File
                  </a>
                </div>
              </div>
            )}

            <div>
              <label className="label">
                <span className="label-text font-semibold">Created At</span>
              </label>
              <p className="text-base-content">
                {new Date(avatarAppearance.createdAt).toLocaleString()}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Updated At</span>
              </label>
              <p className="text-base-content">
                {new Date(avatarAppearance.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>

          {avatarAppearance.uniqueSpeakers &&
            avatarAppearance.uniqueSpeakers.length > 0 &&
            avatarAppearance.avatarDisplayName && (
              <SpeakerIdentificationTable
                avatarAppearanceId={avatarAppearance.id}
                uniqueSpeakers={avatarAppearance.uniqueSpeakers}
                initialMappings={avatarAppearance.speakerMappings || {}}
                avatarId={avatarAppearance.avatarId}
                avatarDisplayName={avatarAppearance.avatarDisplayName}
              />
            )}

          {avatarAppearance.transcriptText && (
            <div className="mt-6">
              <label className="label">
                <span className="label-text font-semibold">Transcript</span>
              </label>
              <div className="bg-base-200 p-4 rounded-lg">
                <p className="text-base-content whitespace-pre-wrap">
                  {avatarAppearance.transcriptText}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
